import {
  Button,
  Menu,
  Icon,
  Flex,
  MenuButton,
  Placement,
  Portal,
  LayoutProps,
  Text,
  MenuList,
  MenuItem,
  Box,
  SkeletonText,
  MenuOptionGroup,
} from '@chakra-ui/react';
import { FC, ReactNode } from 'react';
import { InView } from 'react-intersection-observer';

import ChevronDownIcon from '@/assets/icons/ic-caret-down.svg';
import CheckMarkIcon from '@/assets/icons/ic-check-mark.svg';

import { ListResponse } from '@/lib/datx-jsonapi-react/interfaces/ListResponse';
import { ThinEventSession } from '@/resources/ThinEventSession';

export interface IOption {
  id: string;
  value: string;
}

export interface IDropdownSelect extends LayoutProps {
  options: Array<IOption>;
  placement?: Placement;
  selectedOptions: Array<IOption>;
  menuButtonSlot?: ReactNode;
  setSize?: (size: number) => void;
  size?: number;
  isLoading?: boolean;
  error?: ListResponse<ThinEventSession>;
  isReachingEnd?: boolean;
  defaultLabel: string;

  onClickOption(item: IOption): void;
}

export const DropdownMultiSelect: FC<IDropdownSelect> = ({
  options,
  placement,
  onClickOption,
  menuButtonSlot,
  selectedOptions,
  setSize,
  isLoading,
  error,
  isReachingEnd,
  defaultLabel,
  size,
  ...rest
}) => {
  const defaultValue = {
    id: null,
    value: defaultLabel,
  };

  const buttonLabel = () => {
    if (selectedOptions.length === 1) {
      return selectedOptions[0].value;
    } else if (selectedOptions.length === 0) {
      return defaultLabel;
    } else {
      return `${selectedOptions.length} selected`;
    }
  };

  return (
    <Menu closeOnSelect={false} placement={placement} isLazy>
      {menuButtonSlot ? (
        menuButtonSlot
      ) : (
        <MenuButton
          as={Button}
          size="sm"
          variant="menu"
          rightIcon={<ChevronDownIcon />}
          w="100%"
          _hover={{ backgroundColor: 'none' }}
          {...rest}
        >
          <Text fontFamily="Space Grotesk" noOfLines={1}>
            {buttonLabel()}
          </Text>
        </MenuButton>
      )}
      <Portal>
        <MenuList maxH={{ base: '250px', md: '400px' }} overflow="auto" maxW={['300px', '444px']} zIndex="dropdown">
          <MenuOptionGroup>
            <MenuItem onClick={() => onClickOption(defaultValue)}>
              <Flex w="100%" justify="space-between">
                <Text noOfLines={1}>{defaultValue.value}</Text>
                <Box>{selectedOptions.length === 0 && <Icon as={CheckMarkIcon} color="violet.500" ml="auto" />}</Box>
              </Flex>
            </MenuItem>
            {options.map((option, index) => (
              <MenuItem key={`${index}_${option.id}`} onClick={() => onClickOption(option)}>
                <Flex w="100%" justify="space-between">
                  <Text noOfLines={1}>{option.value}</Text>
                  <Box>
                    {selectedOptions.find((so) => so.id === option.id) && (
                      <Icon as={CheckMarkIcon} color="violet.500" ml="auto" />
                    )}
                  </Box>
                </Flex>
              </MenuItem>
            ))}
          </MenuOptionGroup>
          {isLoading &&
            !isReachingEnd &&
            Array.from({ length: 10 }, (_, index) => (
              <SkeletonText key={index} css={{ margin: '24px 32px' }} noOfLines={2} />
            ))}
          <InView
            as="span"
            rootMargin="20px 0px 0px 0px"
            onChange={(inView) => {
              if (inView && !error && !isLoading) {
                setSize && setSize(size + 1);
              }
            }}
          >
            <span />
          </InView>
        </MenuList>
      </Portal>
    </Menu>
  );
};
